import type { Conversation } from '../types/chat.types'

export type GroupLabel = 'Pinned' | 'Today' | 'Yesterday' | 'Last week' | string

export interface ConversationGroup {
  label: GroupLabel
  conversations: Conversation[]
}

function getLocalDayBounds(date: Date): { start: Date; end: Date } {
  const start = new Date(date)
  start.setHours(0, 0, 0, 0)

  const end = new Date(date)
  end.setHours(23, 59, 59, 999)

  return { start, end }
}

export function isToday(date: Date): boolean {
  const now = new Date()
  const { start, end } = getLocalDayBounds(now)
  return date >= start && date <= end
}

export function isYesterday(date: Date): boolean {
  const yesterday = new Date()
  yesterday.setDate(yesterday.getDate() - 1)
  const { start, end } = getLocalDayBounds(yesterday)
  return date >= start && date <= end
}

export function isLastWeek(date: Date): boolean {
  if (isToday(date) || isYesterday(date)) {
    return false
  }

  const now = new Date()
  const sevenDaysAgo = new Date(now)
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)
  sevenDaysAgo.setHours(0, 0, 0, 0)

  return date >= sevenDaysAgo
}

export function formatMonthYear(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
}

export function groupConversations(conversations: Conversation[]): ConversationGroup[] {
  const todayGroup: Conversation[] = []
  const yesterdayGroup: Conversation[] = []
  const lastWeekGroup: Conversation[] = []
  const monthYearGroups = new Map<string, Conversation[]>()

  for (const conversation of conversations) {
    const date = new Date(conversation.updatedAt)

    if (isToday(date)) {
      todayGroup.push(conversation)
    } else if (isYesterday(date)) {
      yesterdayGroup.push(conversation)
    } else if (isLastWeek(date)) {
      lastWeekGroup.push(conversation)
    } else {
      const monthYear = formatMonthYear(date)
      if (!monthYearGroups.has(monthYear)) {
        monthYearGroups.set(monthYear, [])
      }
      monthYearGroups.get(monthYear)!.push(conversation)
    }
  }

  const result: ConversationGroup[] = []

  if (todayGroup.length > 0) {
    result.push({ label: 'Today', conversations: todayGroup })
  }

  if (yesterdayGroup.length > 0) {
    result.push({ label: 'Yesterday', conversations: yesterdayGroup })
  }

  if (lastWeekGroup.length > 0) {
    result.push({ label: 'Last week', conversations: lastWeekGroup })
  }

  const monthYearEntries = Array.from(monthYearGroups.entries())
  monthYearEntries.sort((a, b) => {
    const dateA = new Date(a[1][0]!.updatedAt)
    const dateB = new Date(b[1][0]!.updatedAt)
    return dateB.getTime() - dateA.getTime()
  })

  for (const [label, groupedConversations] of monthYearEntries) {
    result.push({ label, conversations: groupedConversations })
  }

  return result
}

